// pages/overlay/FaceCam.jsx
// Minimal persistent overlay for talking-head scenes. Doesn't compete with
// Dan's face — just brand presence + an optional live queue counter.
//
// Two elements only:
//   1. Bottom-left: JoP logo bug + "thejoyofpancakes.com" (always visible)
//   2. Bottom-right: "🎟 N in queue" badge (toggle via showQueueCounterOnFaceCam)
//
// No background. Transparent everywhere. The webcam feed IS the scene.

import { useEffect, useRef, useState } from "react";
import useOrientation from "./../../hooks/useOrientation";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";

export default function FaceCam() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const config = useStreamConfig();
  const { allActive } = useCurrentShiftOrders();

  const showCounter = config.showQueueCounterOnFaceCam !== false;
  const queueCount = allActive.length;

  return (
    <div style={{
      width: "100vw",
      height: "100vh",
      background: "transparent",
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSize: "calc(16px + 0.55vw)",
      userSelect: "none",
      overflow: "hidden",
      position: "relative",
      pointerEvents: "none",
    }}>
      {/* Bottom-left: logo bug */}
      <div style={{
        position: "absolute",
        bottom: portrait ? "1rem" : "1.5rem",
        left: portrait ? "1rem" : "1.5rem",
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        // Solid frost instead of backdrop-filter: blur. A backdrop-filter over
        // the LIVE camera feed re-samples + re-composites every frame (the feed
        // changes constantly), so a "static" blur here is a per-frame GPU cost.
        // A slightly more opaque fill reads the same on a talking-head scene.
        background: "rgba(var(--jop-stage-rgb), 0.85)",
        borderRadius: "999px",
        padding: "0.75rem 1.5rem 0.75rem 1rem",
        border: "1px solid rgba(var(--jop-gold-rgb),0.25)",
        boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
        animation: "jopFaceCamFadeIn 1s ease-out",
      }}>
        <img
          src="/jop_logo_wide.png"
          alt="JoP"
          style={{
            height: portrait ? "44px" : "56px",
            objectFit: "contain",
          }}
        />
        <span style={{
          color: "rgba(255,255,255,0.75)",
          fontSize: portrait ? "1rem" : "1.3rem",
          fontWeight: 600,
          letterSpacing: "0.02em",
          fontFamily: "var(--jop-display)",
        }}>
          thejoyofpancakes.com
        </span>
      </div>

      {/* Bottom-right: queue counter badge */}
      {showCounter && (
        queueCount > 0 ? (
        <div style={{
          position: "absolute",
          bottom: portrait ? "1rem" : "1.5rem",
          right: portrait ? "1rem" : "1.5rem",
          // Solid frost, not backdrop-filter — see logo-bug note above. A blur
          // over the live camera feed recomposites every frame.
          background: "rgba(var(--jop-stage-rgb), 0.85)",
          borderRadius: "999px",
          padding: "0.5rem 1rem",
          border: "2px solid rgba(var(--jop-gold-rgb),0.4)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          animation: "jopFaceCamFadeIn 1s ease-out",
        }}>
          <span style={{ fontSize: portrait ? "0.85rem" : "0.95rem" }}>🎟️</span>
          <QueueNumber count={queueCount} portrait={portrait} />
        </div>
        ) : (
        <div style={{
          position: "absolute",
          bottom: portrait ? "1rem" : "1.5rem",
          right: portrait ? "1rem" : "1.5rem",
          // Solid frost, not backdrop-filter — see logo-bug note above. A blur
          // over the live camera feed recomposites every frame.
          background: "rgba(var(--jop-stage-rgb), 0.85)",
          borderRadius: "999px",
          padding: "0.5rem 1rem",
          border: "2px solid rgba(var(--jop-gold-rgb),0.4)",
          boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          animation: "jopFaceCamFadeIn 1s ease-out",
          color: "var(--jop-gold)",
          fontWeight: 700,
          fontSize: portrait ? "0.95rem" : "1.1rem",
          fontFamily: "var(--jop-display)",
        }}>
          🎟️ Queue open!
        </div>
        )
      )}

      <style>{`
        @keyframes jopFaceCamFadeIn {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Animated number — does a small pop when the count changes
function QueueNumber({ count, portrait }) {
  const [pop, setPop] = useState(false);
  const prevRef = useRef(count);

  useEffect(() => {
    if (count !== prevRef.current) {
      setPop(true);
      const t = setTimeout(() => setPop(false), 350);
      prevRef.current = count;
      return () => clearTimeout(t);
    }
  }, [count]);

  return (
    <span style={{
      color: "var(--jop-highlight-blue)",
      fontWeight: 800,
      fontSize: portrait ? "0.95rem" : "1.1rem",
      fontVariantNumeric: "tabular-nums",
      fontFamily: "var(--jop-display)",
      transition: "transform 0.2s cubic-bezier(0.34,1.56,0.64,1)",
      transform: pop ? "scale(1.3)" : "scale(1)",
      display: "inline-block",
    }}>
      {count} waiting
    </span>
  );
}
